/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogProps, DialogSurface, DialogTitle, DialogTrigger, Input, InputOnChangeData, makeStyles } from "@fluentui/react-components";
import { RenameRegular } from "@fluentui/react-icons";
import { Column, Row } from "../common";
import { CaseDocument } from "../../data/types";
import { useDialogControls } from "./useDialogControls";

const useStyles = makeStyles({
    root: {
        maxWidth: '400px'
    },
    body: {
        maxWidth: 'inherit',
        width: 'inherit',
        paddingInline: '.5rem'
    },
    input: {
        width: '100%'
    }
});

export type RenameDocDialogProps = Partial<DialogProps> & {
    children?: JSX.Element;
    doc?: CaseDocument;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    onClickTrigger?: (e: React.MouseEvent<any> | React.KeyboardEvent<any>) => void;
    onModifyDocument?: (doc: CaseDocument) => Promise<void>;
}
export const RenameDocDialog = forwardRef((props: RenameDocDialogProps, _ref) => {

    const { children, modalType, open, onOpenChange, doc, onModifyDocument } = props;

    //State
    const [docName, setDocName] = useState<string>(doc?.name || '');

    //Styles
    const styles = useStyles();

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open, onOpenChange);

    //UI Events
    const onChange = (_ev: any, data: InputOnChangeData) => {
        setDocName(data.value);
    }
    const onKeyUp = (event: any) => {
        if (event.key === "Enter") {
            closeDialog();
            onSubmit();
        }
    }
    const onSubmit = async (e?: any) => {
        !!e && e.stopPropagation();
        const newDoc: CaseDocument = { ...doc, name: docName.trim() };

        //save doc
        !!onModifyDocument && await onModifyDocument(newDoc);
    }
    const onCancel = (e: any) => {
        e.stopPropagation();
        setDocName(doc?.name || '');
        closeDialog();
    }

    //Effects
    useEffect(() => {
        if (dialogOpen && !!doc) {
            setDocName(doc?.name || '');
        }
    }, [dialogOpen, doc]);

    return (
        <>
            <Dialog modalType={modalType} open={dialogOpen} onOpenChange={onOpenChangeHandler}>
                <DialogTrigger disableButtonEnhancement>
                    {children}
                </DialogTrigger>
                <DialogSurface aria-describedby={undefined} mountNode={undefined} className={styles.root}>
                    <DialogBody className={styles.body}>
                        <DialogTitle>
                            <Row gap='medium'>
                                <Column width='auto' vAlign='middle'>
                                    {<RenameRegular />}
                                </Column>
                                <Column>
                                    Rename Document
                                </Column>
                            </Row>
                        </DialogTitle>
                        <DialogContent>
                            <Input
                                className={styles.input}
                                value={docName}
                                onChange={onChange}
                                onKeyUp={onKeyUp}
                                data-testid='doc-rename-dialog-name-input'
                            />
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button type="submit" appearance="primary" onClick={onSubmit} data-testid='doc-rename-dialog-submit-button'>
                                    Submit
                                </Button>
                            </DialogTrigger>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary" onClick={onCancel} data-testid='doc-rename-dialog-cancel-button'>
                                    Cancel
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
});